/**
 * "Import staff" on the Staff page (MB's R78; help `cloud.staff`; docs/specs/cloud/tjr-register-
 * import.md). The editor chooses the previous reporting program's list file `tjf.dat`; Teslyn reads its pilot, operator and manager lists
 * in the browser (nothing is uploaded), matches every name with the register (case and spacing ignored) and shows
 * what would be added, ticked as the list marks the person current. A name of someone deleted from the register
 * is matched to them and not added (decision 604): a tenant admin or manager restores them. Only names and roles
 * are written.
 */
import { useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  REGISTER_ROLE_LABEL,
  TjrFileError,
  planStaffImport,
  readTjrDefaults,
  staffImportChanges,
  type Person,
  type RegisterRole,
  type StaffImportRow,
} from '@teslyn/domain';
import { Button } from '../components/Button.tsx';
import { TickCell } from '../components/TickCell.tsx';
import { fileBytes } from '../components/fileBytes.ts';
import { importStaff } from './api.ts';
import { TableWrap } from '../components/TableWrap.tsx';

export interface StaffImportPanelProps {
  readonly sb: SupabaseClient;
  readonly tenantId: string;
  readonly people: readonly Person[];
  readonly onDone: (message: string) => void;
  readonly onCancel: () => void;
}

const rolesText = (roles: readonly RegisterRole[]): string =>
  roles.map((r) => REGISTER_ROLE_LABEL[r]).join(', ');

const TJR_WORD = (r: StaffImportRow): string =>
  r.current === null ? 'Manager list' : r.current ? 'Current (Y)' : 'Left (N)';

const OUTCOME = (r: StaffImportRow): string =>
  r.status === 'new'
    ? 'Add'
    : r.status === 'add_roles'
      ? `Add the role ${rolesText(r.missingRoles)}${r.missingPhone ? ' and the phone number' : ''}`
      : r.status === 'add_phone'
        ? 'Add the phone number'
        : r.status === 'deleted'
          ? `Not added: ${r.note}.`
          : 'Already in the register';

export function StaffImportPanel({
  sb,
  tenantId,
  people,
  onDone,
  onCancel,
}: StaffImportPanelProps): ReactElement {
  const [rows, setRows] = useState<StaffImportRow[] | null>(null);
  const [fileName, setFileName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function choose(file: File | undefined): Promise<void> {
    if (!file) return;
    setError(null);
    setRows(null);
    setFileName(file.name);
    try {
      setRows(planStaffImport(readTjrDefaults(await fileBytes(file)), people));
    } catch (e) {
      setError(e instanceof TjrFileError ? e.message : `The file could not be read: ${String(e)}`);
    }
  }

  async function apply(): Promise<void> {
    if (!rows) return;
    setBusy(true);
    setError(null);
    try {
      const { added, rolesGiven } = await importStaff(sb, tenantId, staffImportChanges(rows, people));
      const parts = [
        added > 0 ? `added ${added} ${added === 1 ? 'person' : 'people'}` : '',
        rolesGiven > 0
          ? `gave ${rolesGiven} ${rolesGiven === 1 ? 'person' : 'people'} their roles from the list`
          : '',
      ].filter(Boolean);
      onDone(parts.length > 0 ? `Imported: ${parts.join(' and ')}.` : 'Nothing was imported.');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const adds = (r: StaffImportRow): boolean =>
    r.status === 'new' || r.status === 'add_roles' || r.status === 'add_phone';
  const ticked = rows?.filter((r) => r.selected && adds(r)) ?? [];
  const deletedRows = rows?.filter((r) => r.status === 'deleted') ?? [];
  const toggle = (name: string, on: boolean): void =>
    setRows((list) => (list ?? []).map((r) => (r.name === name ? { ...r, selected: on } : r)));
  const setActive = (name: string, on: boolean): void =>
    setRows((list) => (list ?? []).map((r) => (r.name === name ? { ...r, active: on } : r)));

  return (
    <section className="panel stack" aria-label="Import staff">
      <h2 className="heading">Import staff</h2>
      <p className="body">
        Choose the staff and aircraft list file (<span className="data">tjf.dat</span>), in the previous
        reporting program&apos;s folder. Teslyn reads its pilots, operators and managers on this computer and
        adds the people you tick, with their names and roles only: licences and phone numbers are not
        imported. Someone already in the register (the same name, in any case) is never added twice; they only
        gain a role the list gives them. Someone deleted from the register is not added again: a tenant admin
        or manager can restore them under Deleted people. People who have left are added inactive, so old
        records and later imports find them by name, but they are not offered for new days.
      </p>
      <label className="field">
        <span className="label">Staff and aircraft list file (tjf.dat)</span>
        <input
          type="file"
          accept=".dat"
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = '';
            void choose(f);
          }}
        />
      </label>
      {error && (
        <p className="notice notice--fail" role="alert">
          {error}
        </p>
      )}
      {rows && (
        <>
          <p className="body" role="status">
            {fileName}: {rows.length} names. {rows.filter((r) => r.status === 'new').length} not in the
            register, {rows.filter((r) => r.status === 'add_roles').length} missing a role,{' '}
            {rows.some((r) => r.status === 'add_phone')
              ? `${rows.filter((r) => r.status === 'add_phone').length} missing only the phone number, `
              : ''}
            {rows.filter((r) => r.status === 'in_register').length} already there
            {deletedRows.length > 0 ? `, ${deletedRows.length} deleted from the register` : ''}. Everyone new
            is ticked; &quot;Active&quot; is ticked for the people the list marks Y and for its managers.
          </p>
          {deletedRows.length > 0 && (
            <ul className="notice notice--warn stack" aria-label="Deleted from the staff register">
              {deletedRows.map((r) => (
                <li key={r.name}>{r.note}.</li>
              ))}
            </ul>
          )}
          <TableWrap>
            <table className="table" aria-label="Staff to import">
              <thead>
                <tr>
                  <th className="label">
                    <span className="sr-only">Import</span>
                  </th>
                  <th className="label">Name</th>
                  <th className="label">Roles</th>
                  <th className="label">In the list</th>
                  <th className="label">Active</th>
                  <th className="label">What happens</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.name}>
                    <td>
                      {adds(r) && (
                        <TickCell
                          aria-label={`Import ${r.name}`}
                          checked={r.selected}
                          onChange={(e) => toggle(r.name, e.target.checked)}
                        />
                      )}
                    </td>
                    <td>{r.name}</td>
                    <td>{rolesText(r.roles)}</td>
                    <td>{TJR_WORD(r)}</td>
                    <td>
                      {r.status === 'new' && (
                        <TickCell
                          aria-label={`${r.name} active`}
                          checked={r.active}
                          onChange={(e) => setActive(r.name, e.target.checked)}
                        />
                      )}
                    </td>
                    <td>
                      {OUTCOME(r)}
                      {r.status === 'new' && !r.active ? ' (inactive)' : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>
        </>
      )}
      <div className="row">
        <Button variant="primary" busy={busy} disabled={ticked.length === 0} onClick={() => void apply()}>
          {ticked.length === 1 ? 'Import 1 person' : `Import ${ticked.length} people`}
        </Button>
        <Button variant="quiet" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </section>
  );
}
